import { watchEffect } from 'vue'
import type { Pinia } from 'pinia'
import type { Router } from 'vue-router'
import { resolveAssetUrl } from '@/adapters/legacy/loadLegacyAudience'
import { useAudienceStore } from '@/app/stores/audience'
import { ImagePreloadController, type ImagePreloadGroups } from '@/core/media/imagePreload'
import type { AssetRef } from '@/domain/audience/types'

export function installAudienceImagePreload(pinia: Pinia, router: Router): () => void {
  const audience = useAudienceStore(pinia)
  const controller = new ImagePreloadController()
  const stop = watchEffect(() => {
    if (audience.status !== 'ready' || audience.previewMode) {
      controller.replaceScope(`inactive:${audience.status}:${audience.previewMode}`, {})
      return
    }
    const currentRoute = router.currentRoute.value
    const scope = buildAudienceImagePreloadScope(
      String(currentRoute.name || ''),
      audience,
      { serviceId: String(currentRoute.params.serviceId || '') },
    )
    controller.replaceScope(`${audience.releaseId}:${scope.scopeKey}`, scope.groups)
  })
  return () => { stop(); controller.dispose() }
}

export function buildAudienceImagePreloadScope(
  routeName: string,
  audience: ReturnType<typeof useAudienceStore>,
  routeParams: { serviceId?: string } = {},
): { scopeKey: string; groups: ImagePreloadGroups } {
  const P0: string[] = []
  const P1: string[] = []
  const P2: string[] = []
  const transport = audience.selectedTransport
  const zone = audience.selectedZone
  const overview = audience.config?.overview
  const add = (target: string[], value: AssetRef | string | null | undefined) => {
    const src = preloadDisplayUrl(value)
    if (src && !target.includes(src)) target.push(src)
  }

  switch (routeName) {
    case 'home':
      add(P0, audience.config?.home?.image)
      break
    case 'entry':
    case 'entry-overview':
      add(P0, overview?.entryImage)
      break
    case 'exit':
    case 'exit-overview':
    case 'exit-destinations':
      add(P0, overview?.exitImage)
      break
    case 'entry-transport':
    case 'entry-gate':
      add(P0, audience.selectedEntryRoute?.image || audience.gates.find((gate) => gate.id === audience.gateId)?.image || transport?.entry?.overviewImage)
      for (const route of transport?.entry?.routes || []) add(P1, route.image)
      for (const gate of transport?.entry?.gates || []) add(P1, gate.image)
      for (const item of audience.visibleZones) add(P2, item.image)
      break
    case 'exit-transport':
      add(P0, audience.selectedDestination?.image || transport?.exit?.routeImage)
      for (const destination of audience.destinations) add(P1, destination.image)
      break
    case 'entry-zones':
      add(P0, audience.config?.zones?.overviewImage)
      for (const item of audience.visibleZones) add(P1, item.image)
      break
    case 'entry-zone':
      add(P0, zone?.image)
      add(P1, transport?.entry?.overviewImage)
      break
    case 'route-summary':
      add(P0, audience.mode === 'entry'
        ? audience.selectedEntryRoute?.image || audience.gates.find((gate) => gate.id === audience.gateId)?.image || transport?.entry?.overviewImage
        : audience.selectedDestination?.image || transport?.exit?.routeImage)
      if (audience.mode === 'entry') add(P0, zone?.image)
      break
    case 'service-detail': {
      const serviceId = String(routeParams.serviceId || '')
      if (serviceId === 'order-promotion') {
        add(P0, audience.config?.services?.orderPromotion?.menuImage)
        add(P0, audience.config?.services?.orderPromotion?.orderQrImage)
        break
      }
      const service = audience.config?.services?.items?.find((item) => item.id === serviceId)
      add(P0, service?.image)
      break
    }
  }

  return {
    scopeKey: [routeName, routeParams.serviceId || '', audience.mode, audience.transportId, audience.originId, audience.gateId, audience.destinationId, audience.zoneId].join(':'),
    groups: { P0, P1, P2 },
  }
}

function preloadDisplayUrl(value: AssetRef | string | null | undefined): string {
  const src = resolveAssetUrl(value).trim()
  return src.startsWith('data:') || src.startsWith('blob:') ? '' : src
}
